import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { 
  Save, RefreshCw, Plus, Trash2, ChevronDown, ChevronUp,
  Crown, Globe, BookOpen, Trophy, Calendar, Users, Star,
  GraduationCap, Sparkles, Eye, ArrowLeft, Film, Clapperboard, FolderOpen
} from 'lucide-react';
import { Link } from 'react-router-dom';
import { MediaLibraryPicker } from '@/components/admin/MediaLibraryPicker';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Icon mapping for achievements
const ICON_OPTIONS = ['Crown', 'Globe', 'BookOpen', 'Trophy', 'Star', 'Award', 'Flame', 'Zap', 'Film', 'Clapperboard'];
const STAT_ICON_OPTIONS = ['Calendar', 'Users', 'Globe', 'Crown', 'Star', 'Trophy'];
const GLOW_OPTIONS = [
  { value: 'from-yellow-500/30', label: 'Yellow' },
  { value: 'from-green-500/30', label: 'Green' },
  { value: 'from-blue-500/30', label: 'Blue' },
  { value: 'from-red-500/30', label: 'Red' },
  { value: 'from-purple-500/30', label: 'Purple' },
  { value: 'from-amber-500/30', label: 'Amber' }
];

// Collapsible Section Component
const CollapsibleSection = ({ title, children, defaultOpen = false }) => {
  const [isOpen, setIsOpen] = useState(defaultOpen);
  
  return (
    <div className="bg-zinc-900 border border-zinc-800 rounded-xl overflow-hidden">
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="w-full px-6 py-4 flex items-center justify-between text-left hover:bg-zinc-800/50 transition-colors"
      >
        <h3 className="text-lg font-bold text-white">{title}</h3>
        {isOpen ? (
          <ChevronUp className="w-5 h-5 text-zinc-400" />
        ) : (
          <ChevronDown className="w-5 h-5 text-zinc-400" />
        )}
      </button>
      {isOpen && (
        <div className="px-6 pb-6 border-t border-zinc-800 pt-4">
          {children}
        </div>
      )}
    </div>
  );
};

// Input Field Component — uses internal state so typing never re-renders the
// giant parent editor. Parent is updated only on blur / enter.
const InputField = React.memo(({ label, value, onChange, placeholder, type = 'text', textarea = false }) => {
  const [local, setLocal] = useState(value ?? '');
  // Sync when the external value changes (e.g. after a Reset or load)
  React.useEffect(() => { setLocal(value ?? ''); }, [value]);
  const commit = () => {
    if (local !== (value ?? '')) onChange(local);
  };
  return (
    <div className="space-y-2">
      <label className="text-sm font-medium text-zinc-400">{label}</label>
      {textarea ? (
        <textarea
          value={local}
          onChange={(e) => setLocal(e.target.value)}
          onBlur={commit}
          placeholder={placeholder}
          rows={4}
          className="w-full px-4 py-3 bg-zinc-800 border border-zinc-700 rounded-lg text-white placeholder-zinc-500 focus:border-yellow-500 focus:outline-none resize-none"
        />
      ) : (
        <input
          type={type}
          value={local}
          onChange={(e) => setLocal(e.target.value)}
          onBlur={commit}
          onKeyDown={(e) => { if (e.key === 'Enter' && type !== 'textarea') e.currentTarget.blur(); }}
          placeholder={placeholder}
          className="w-full px-4 py-3 bg-zinc-800 border border-zinc-700 rounded-lg text-white placeholder-zinc-500 focus:border-yellow-500 focus:outline-none"
        />
      )}
    </div>
  );
});

// Select Field Component
const SelectField = React.memo(({ label, value, onChange, options }) => (
  <div className="space-y-2">
    <label className="text-sm font-medium text-zinc-400">{label}</label>
    <select
      value={value || ''}
      onChange={(e) => onChange(e.target.value)}
      className="w-full px-4 py-3 bg-zinc-800 border border-zinc-700 rounded-lg text-white focus:border-yellow-500 focus:outline-none"
    >
      {options.map(opt => (
        <option key={typeof opt === 'string' ? opt : opt.value} value={typeof opt === 'string' ? opt : opt.value}>
          {typeof opt === 'string' ? opt : opt.label}
        </option>
      ))}
    </select>
  </div>
));

// Image Field with Media Library Picker
const ImageField = ({ label, value, onChange }) => {
  const [showPicker, setShowPicker] = useState(false);

  return (
    <div className="space-y-2">
      <label className="text-sm font-medium text-zinc-400">{label}</label>
      <div className="flex gap-2 flex-wrap">
        <input
          type="text"
          value={value || ''}
          onChange={(e) => onChange(e.target.value)}
          placeholder="Image URL or click Library to pick/upload"
          className="flex-1 min-w-[200px] px-4 py-3 bg-zinc-800 border border-zinc-700 rounded-lg text-white placeholder-zinc-500 focus:border-yellow-500 focus:outline-none"
        />
        <button
          type="button"
          onClick={() => setShowPicker(true)}
          className="px-4 py-3 bg-yellow-500/20 hover:bg-yellow-500/30 text-yellow-500 rounded-lg flex items-center gap-2 transition-colors"
          data-testid="image-library-btn"
        >
          <FolderOpen className="w-4 h-4" />
          <span className="text-sm font-medium">Library</span>
        </button>
      </div>
      {value && (
        <div className="mt-2 relative w-32 h-20 rounded-lg overflow-hidden bg-zinc-800">
          <img
            src={value.startsWith('http') ? value : `${API_URL}${value}`}
            alt=""
            className="w-full h-full object-cover"
          />
        </div>
      )}
      <MediaLibraryPicker
        isOpen={showPicker}
        onClose={() => setShowPicker(false)}
        onSelect={(items) => {
          // MediaLibraryPicker always calls onSelect with an array,
          // even for single selection.
          const picked = Array.isArray(items) ? items[0] : items;
          if (picked?.url) onChange(picked.url);
          setShowPicker(false);
        }}
        allowedTypes={['image']}
        multiple={false}
      />
    </div>
  );
};

export default function AdminAboutEditor() {
  const [content, setContent] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState(null);

  useEffect(() => {
    fetchContent();
  }, []);

  const fetchContent = async () => {
    try {
      const response = await fetch(`${API_URL}/api/about-editor`);
      const data = await response.json();
      setContent(data);
    } catch (error) {
      console.error('Error fetching about content:', error);
      setMessage({ type: 'error', text: 'Failed to load content' });
    } finally {
      setLoading(false);
    }
  };

  const saveContent = async () => {
    setSaving(true);
    try {
      const response = await fetch(`${API_URL}/api/about-editor`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(content)
      });
      
      if (response.ok) {
        setMessage({ type: 'success', text: 'Content saved successfully!' });
        setTimeout(() => setMessage(null), 3000);
      } else {
        throw new Error('Failed to save');
      }
    } catch (error) {
      console.error('Error saving content:', error);
      setMessage({ type: 'error', text: 'Failed to save content' });
    } finally {
      setSaving(false);
    }
  };

  const resetContent = async () => {
    if (!window.confirm('Are you sure you want to reset to default content? This cannot be undone.')) {
      return;
    }
    
    setLoading(true);
    try {
      const response = await fetch(`${API_URL}/api/about-editor/reset`, {
        method: 'POST'
      });
      const data = await response.json();
      setContent(data.content);
      setMessage({ type: 'success', text: 'Content reset to defaults' });
      setTimeout(() => setMessage(null), 3000);
    } catch (error) {
      console.error('Error resetting content:', error);
      setMessage({ type: 'error', text: 'Failed to reset content' });
    } finally {
      setLoading(false);
    }
  };

  const updateField = (section, field, value) => {
    setContent(prev => ({
      ...prev,
      [section]: {
        ...prev[section],
        [field]: value
      }
    }));
  };

  const updateArrayItem = (section, arrayField, index, field, value) => {
    setContent(prev => ({
      ...prev,
      [section]: {
        ...prev[section],
        [arrayField]: prev[section][arrayField].map((item, i) => 
          i === index ? { ...item, [field]: value } : item
        )
      }
    }));
  };

  const addArrayItem = (section, arrayField, template) => {
    setContent(prev => ({
      ...prev,
      [section]: {
        ...prev[section],
        [arrayField]: [...(prev[section][arrayField] || []), template]
      }
    }));
  };

  const removeArrayItem = (section, arrayField, index) => {
    setContent(prev => ({
      ...prev,
      [section]: {
        ...prev[section],
        [arrayField]: prev[section][arrayField].filter((_, i) => i !== index)
      }
    }));
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-black flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-yellow-500"></div>
      </div>
    );
  }

  if (!content) {
    return (
      <div className="min-h-screen bg-black flex items-center justify-center">
        <p className="text-white">Failed to load content</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-black text-white">
      {/* Header */}
      <div className="sticky top-0 z-50 bg-black/90 backdrop-blur-sm border-b border-zinc-800">
        <div className="max-w-6xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Link to="/admin/dashboard" className="p-2 hover:bg-zinc-800 rounded-lg transition-colors">
              <ArrowLeft className="w-5 h-5" />
            </Link>
            <div>
              <h1 className="text-xl font-bold">Legacy / Mestre Page Editor</h1>
              <p className="text-sm text-zinc-400">Edits the public /legacy page — Mestre Curtis founder story</p>
            </div>
          </div>
          
          <div className="flex items-center gap-3">
            {message && (
              <motion.div
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                className={`px-4 py-2 rounded-lg text-sm font-medium ${
                  message.type === 'success' ? 'bg-green-500/20 text-green-400' : 'bg-red-500/20 text-red-400'
                }`}
              >
                {message.text}
              </motion.div>
            )}
            
            <Link
              to="/legacy"
              target="_blank"
              className="px-4 py-2 bg-zinc-800 hover:bg-zinc-700 rounded-lg flex items-center gap-2 transition-colors"
            >
              <Eye className="w-4 h-4" />
              Preview
            </Link>
            
            <button
              onClick={resetContent}
              className="px-4 py-2 bg-zinc-800 hover:bg-zinc-700 rounded-lg flex items-center gap-2 transition-colors"
            >
              <RefreshCw className="w-4 h-4" />
              Reset
            </button>
            
            <button
              onClick={saveContent}
              disabled={saving}
              className="px-6 py-2 bg-gradient-to-r from-yellow-500 to-amber-500 hover:from-yellow-400 hover:to-amber-400 text-black font-bold rounded-lg flex items-center gap-2 transition-all disabled:opacity-50"
            >
              <Save className="w-4 h-4" />
              {saving ? 'Saving...' : 'Save Changes'}
            </button>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-6xl mx-auto px-6 py-8 space-y-6">
        
        {/* Hero Section */}
        <CollapsibleSection title="Hero Section" defaultOpen={true}>
          <div className="grid md:grid-cols-2 gap-4">
            <InputField
              label="Pre-Title (e.g., 'THE LEGEND')"
              value={content.hero?.preTitle}
              onChange={(v) => updateField('hero', 'preTitle', v)}
            />
            <InputField
              label="Main Title"
              value={content.hero?.mainTitle}
              onChange={(v) => updateField('hero', 'mainTitle', v)}
            />
            <InputField
              label="Name"
              value={content.hero?.name}
              onChange={(v) => updateField('hero', 'name', v)}
            />
            <InputField
              label="Ambassador Title"
              value={content.hero?.ambassadorTitle}
              onChange={(v) => updateField('hero', 'ambassadorTitle', v)}
            />
            <InputField
              label="Subtitle"
              value={content.hero?.subtitle}
              onChange={(v) => updateField('hero', 'subtitle', v)}
            />
            <ImageField
              label="Background Image URL"
              value={content.hero?.backgroundImage}
              onChange={(v) => updateField('hero', 'backgroundImage', v)}
            />
            <InputField
              label="Badge 1 (Left)"
              value={content.hero?.badge1}
              onChange={(v) => updateField('hero', 'badge1', v)}
            />
            <InputField
              label="Badge 2 (Right)"
              value={content.hero?.badge2}
              onChange={(v) => updateField('hero', 'badge2', v)}
            />
          </div>
          <div className="mt-4">
            <InputField
              label="Hero Description"
              value={content.hero?.description}
              onChange={(v) => updateField('hero', 'description', v)}
              textarea
            />
          </div>
        </CollapsibleSection>

        {/* Achievements Section */}
        <CollapsibleSection title="Achievements Section">
          <div className="space-y-4">
            <div className="grid md:grid-cols-2 gap-4">
              <InputField
                label="Section Label"
                value={content.achievementSection?.sectionLabel}
                onChange={(v) => updateField('achievementSection', 'sectionLabel', v)}
              />
              <InputField
                label="Section Title"
                value={content.achievementSection?.title}
                onChange={(v) => updateField('achievementSection', 'title', v)}
              />
            </div>
            <InputField
              label="Section Description"
              value={content.achievementSection?.description}
              onChange={(v) => updateField('achievementSection', 'description', v)}
              textarea
            />
            
            <div className="border-t border-zinc-800 pt-4 mt-4">
              <div className="flex items-center justify-between mb-4">
                <h4 className="font-semibold text-white">Achievements</h4>
                <button
                  onClick={() => addArrayItem('achievementSection', 'achievements', {
                    icon: 'Star',
                    title: 'New Achievement',
                    description: 'Description here',
                    glow: 'from-yellow-500/30'
                  })}
                  className="px-3 py-1 bg-yellow-500/20 text-yellow-400 rounded-lg text-sm flex items-center gap-1 hover:bg-yellow-500/30"
                >
                  <Plus className="w-4 h-4" /> Add Achievement
                </button>
              </div>
              
              {content.achievementSection?.achievements?.map((achievement, index) => (
                <div key={index} className="bg-zinc-800/50 rounded-lg p-4 mb-3">
                  <div className="flex justify-between items-start mb-3">
                    <span className="text-sm text-zinc-400">Achievement {index + 1}</span>
                    <button
                      onClick={() => removeArrayItem('achievementSection', 'achievements', index)}
                      className="p-1 hover:bg-red-500/20 rounded text-red-400"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                  <div className="grid md:grid-cols-3 gap-3">
                    <SelectField
                      label="Icon"
                      value={achievement.icon}
                      onChange={(v) => updateArrayItem('achievementSection', 'achievements', index, 'icon', v)}
                      options={ICON_OPTIONS}
                    />
                    <InputField
                      label="Title"
                      value={achievement.title}
                      onChange={(v) => updateArrayItem('achievementSection', 'achievements', index, 'title', v)}
                    />
                    <SelectField
                      label="Glow Color"
                      value={achievement.glow}
                      onChange={(v) => updateArrayItem('achievementSection', 'achievements', index, 'glow', v)}
                      options={GLOW_OPTIONS}
                    />
                  </div>
                  <div className="mt-3">
                    <InputField
                      label="Description"
                      value={achievement.description}
                      onChange={(v) => updateArrayItem('achievementSection', 'achievements', index, 'description', v)}
                      textarea
                    />
                  </div>
                </div>
              ))}
            </div>
          </div>
        </CollapsibleSection>

        {/* Stats Section */}
        <CollapsibleSection title="Stats Section">
          <div className="flex items-center justify-between mb-4">
            <h4 className="font-semibold text-white">Statistics</h4>
            <button
              onClick={() => addArrayItem('stats', 'stats', {
                value: '0',
                label: 'New Stat',
                icon: 'Star'
              })}
              className="px-3 py-1 bg-yellow-500/20 text-yellow-400 rounded-lg text-sm flex items-center gap-1 hover:bg-yellow-500/30"
            >
              <Plus className="w-4 h-4" /> Add Stat
            </button>
          </div>
          
          <div className="grid md:grid-cols-2 gap-4">
            {content.stats?.stats?.map((stat, index) => (
              <div key={index} className="bg-zinc-800/50 rounded-lg p-4">
                <div className="flex justify-between items-start mb-3">
                  <span className="text-sm text-zinc-400">Stat {index + 1}</span>
                  <button
                    onClick={() => removeArrayItem('stats', 'stats', index)}
                    className="p-1 hover:bg-red-500/20 rounded text-red-400"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
                <div className="grid grid-cols-3 gap-3">
                  <InputField
                    label="Value"
                    value={stat.value}
                    onChange={(v) => updateArrayItem('stats', 'stats', index, 'value', v)}
                  />
                  <InputField
                    label="Label"
                    value={stat.label}
                    onChange={(v) => updateArrayItem('stats', 'stats', index, 'label', v)}
                  />
                  <SelectField
                    label="Icon"
                    value={stat.icon}
                    onChange={(v) => updateArrayItem('stats', 'stats', index, 'icon', v)}
                    options={STAT_ICON_OPTIONS}
                  />
                </div>
              </div>
            ))}
          </div>
        </CollapsibleSection>

        {/* Studied With Section */}
        <CollapsibleSection title="Studied With the Masters Section">
          <div className="space-y-4">
            <div className="grid md:grid-cols-2 gap-4">
              <InputField
                label="Section Label"
                value={content.studiedWith?.sectionLabel}
                onChange={(v) => updateField('studiedWith', 'sectionLabel', v)}
              />
              <InputField
                label="Section Title"
                value={content.studiedWith?.title}
                onChange={(v) => updateField('studiedWith', 'title', v)}
              />
            </div>
            <InputField
              label="Section Description"
              value={content.studiedWith?.description}
              onChange={(v) => updateField('studiedWith', 'description', v)}
              textarea
            />
            
            <div className="border-t border-zinc-800 pt-4 mt-4">
              <div className="flex items-center justify-between mb-4">
                <h4 className="font-semibold text-white">Masters / Teachers</h4>
                <button
                  onClick={() => addArrayItem('studiedWith', 'masters', {
                    name: 'New Master',
                    title: 'Title / Location',
                    description: 'Description of what was learned',
                    years: ''
                  })}
                  className="px-3 py-1 bg-yellow-500/20 text-yellow-400 rounded-lg text-sm flex items-center gap-1 hover:bg-yellow-500/30"
                >
                  <Plus className="w-4 h-4" /> Add Master
                </button>
              </div>
              
              {content.studiedWith?.masters?.map((master, index) => (
                <div key={index} className="bg-zinc-800/50 rounded-lg p-4 mb-3">
                  <div className="flex justify-between items-start mb-3">
                    <span className="text-sm text-zinc-400">Master {index + 1}</span>
                    <button
                      onClick={() => removeArrayItem('studiedWith', 'masters', index)}
                      className="p-1 hover:bg-red-500/20 rounded text-red-400"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                  <div className="grid md:grid-cols-2 gap-3 mb-3">
                    <InputField
                      label="Name"
                      value={master.name}
                      onChange={(v) => updateArrayItem('studiedWith', 'masters', index, 'name', v)}
                    />
                    <InputField
                      label="Title / Location"
                      value={master.title}
                      onChange={(v) => updateArrayItem('studiedWith', 'masters', index, 'title', v)}
                    />
                  </div>
                  <InputField
                    label="Years Studied"
                    value={master.years}
                    onChange={(v) => updateArrayItem('studiedWith', 'masters', index, 'years', v)}
                    placeholder="e.g., 15+ years, Since 1994"
                  />
                  <div className="mt-3">
                    <InputField
                      label="Description"
                      value={master.description}
                      onChange={(v) => updateArrayItem('studiedWith', 'masters', index, 'description', v)}
                      textarea
                    />
                  </div>
                </div>
              ))}
            </div>
          </div>
        </CollapsibleSection>

        {/* Film Credits Section */}
        <CollapsibleSection title="Film & Television Credits Section">
          <div className="space-y-4">
            <div className="grid md:grid-cols-2 gap-4">
              <InputField
                label="Section Label"
                value={content.filmCredits?.sectionLabel}
                onChange={(v) => updateField('filmCredits', 'sectionLabel', v)}
              />
              <InputField
                label="Section Title"
                value={content.filmCredits?.title}
                onChange={(v) => updateField('filmCredits', 'title', v)}
              />
            </div>
            <InputField
              label="Section Description"
              value={content.filmCredits?.description}
              onChange={(v) => updateField('filmCredits', 'description', v)}
              textarea
            />
            
            <div className="border-t border-zinc-800 pt-4 mt-4">
              <div className="flex items-center justify-between mb-4">
                <h4 className="font-semibold text-white">Film Credits</h4>
                <button
                  onClick={() => addArrayItem('filmCredits', 'credits', {
                    title: 'New Film',
                    year: '',
                    role: 'Role in production',
                    description: 'Description of involvement',
                    highlight: false
                  })}
                  className="px-3 py-1 bg-purple-500/20 text-purple-400 rounded-lg text-sm flex items-center gap-1 hover:bg-purple-500/30"
                >
                  <Plus className="w-4 h-4" /> Add Film Credit
                </button>
              </div>
              
              {content.filmCredits?.credits?.map((credit, index) => (
                <div key={index} className={`rounded-lg p-4 mb-3 ${credit.highlight ? 'bg-purple-500/10 border border-purple-500/30' : 'bg-zinc-800/50'}`}>
                  <div className="flex justify-between items-start mb-3">
                    <span className="text-sm text-zinc-400">Credit {index + 1}</span>
                    <button
                      onClick={() => removeArrayItem('filmCredits', 'credits', index)}
                      className="p-1 hover:bg-red-500/20 rounded text-red-400"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                  <div className="grid md:grid-cols-3 gap-3 mb-3">
                    <InputField
                      label="Film/Show Title"
                      value={credit.title}
                      onChange={(v) => updateArrayItem('filmCredits', 'credits', index, 'title', v)}
                    />
                    <InputField
                      label="Year"
                      value={credit.year}
                      onChange={(v) => updateArrayItem('filmCredits', 'credits', index, 'year', v)}
                      placeholder="e.g., 2015"
                    />
                    <InputField
                      label="Role"
                      value={credit.role}
                      onChange={(v) => updateArrayItem('filmCredits', 'credits', index, 'role', v)}
                      placeholder="e.g., Cultural Consultant"
                    />
                  </div>
                  <InputField
                    label="Description"
                    value={credit.description}
                    onChange={(v) => updateArrayItem('filmCredits', 'credits', index, 'description', v)}
                    textarea
                  />
                  <label className="flex items-center gap-2 cursor-pointer mt-3">
                    <input
                      type="checkbox"
                      checked={credit.highlight || false}
                      onChange={(e) => updateArrayItem('filmCredits', 'credits', index, 'highlight', e.target.checked)}
                      className="w-4 h-4 rounded border-zinc-600 bg-zinc-800 text-purple-500 focus:ring-purple-500"
                    />
                    <span className="text-sm text-zinc-400">Featured (Highlight this credit)</span>
                  </label>
                </div>
              ))}
            </div>
          </div>
        </CollapsibleSection>

        {/* Brasilian Connection Section */}
        <CollapsibleSection title="Brasilian Connection Section">
          <div className="space-y-4">
            <div className="grid md:grid-cols-2 gap-4">
              <InputField
                label="Section Label"
                value={content.brazilianConnection?.sectionLabel}
                onChange={(v) => updateField('brazilianConnection', 'sectionLabel', v)}
              />
              <InputField
                label="Section Title"
                value={content.brazilianConnection?.title}
                onChange={(v) => updateField('brazilianConnection', 'title', v)}
              />
            </div>
            <InputField
              label="Quote (Portuguese)"
              value={content.brazilianConnection?.quote}
              onChange={(v) => updateField('brazilianConnection', 'quote', v)}
              textarea
            />
            <InputField
              label="Quote Description"
              value={content.brazilianConnection?.quoteDescription}
              onChange={(v) => updateField('brazilianConnection', 'quoteDescription', v)}
              textarea
            />
            
            <div className="border-t border-zinc-800 pt-4 mt-4">
              <div className="flex items-center justify-between mb-4">
                <h4 className="font-semibold text-white">Connections</h4>
                <button
                  onClick={() => addArrayItem('brazilianConnection', 'connections', {
                    name: 'New Connection',
                    role: 'Role description',
                    highlight: false
                  })}
                  className="px-3 py-1 bg-green-500/20 text-green-400 rounded-lg text-sm flex items-center gap-1 hover:bg-green-500/30"
                >
                  <Plus className="w-4 h-4" /> Add Connection
                </button>
              </div>
              
              <div className="grid md:grid-cols-2 gap-3">
                {content.brazilianConnection?.connections?.map((connection, index) => (
                  <div key={index} className={`rounded-lg p-4 ${connection.highlight ? 'bg-green-500/10 border border-green-500/30' : 'bg-zinc-800/50'}`}>
                    <div className="flex justify-between items-start mb-3">
                      <span className="text-sm text-zinc-400">Connection {index + 1}</span>
                      <button
                        onClick={() => removeArrayItem('brazilianConnection', 'connections', index)}
                        className="p-1 hover:bg-red-500/20 rounded text-red-400"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                    <div className="space-y-3">
                      <InputField
                        label="Name"
                        value={connection.name}
                        onChange={(v) => updateArrayItem('brazilianConnection', 'connections', index, 'name', v)}
                      />
                      <InputField
                        label="Role"
                        value={connection.role}
                        onChange={(v) => updateArrayItem('brazilianConnection', 'connections', index, 'role', v)}
                      />
                      <label className="flex items-center gap-2 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={connection.highlight || false}
                          onChange={(e) => updateArrayItem('brazilianConnection', 'connections', index, 'highlight', e.target.checked)}
                          className="w-4 h-4 rounded border-zinc-600 bg-zinc-800 text-green-500 focus:ring-green-500"
                        />
                        <span className="text-sm text-zinc-400">Highlight (NEW badge)</span>
                      </label>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </CollapsibleSection>

        {/* Journey Section */}
        <CollapsibleSection title="Journey Section">
          <div className="space-y-4">
            <div className="grid md:grid-cols-2 gap-4">
              <InputField
                label="Section Label"
                value={content.journey?.sectionLabel}
                onChange={(v) => updateField('journey', 'sectionLabel', v)}
              />
              <InputField
                label="Section Title"
                value={content.journey?.title}
                onChange={(v) => updateField('journey', 'title', v)}
              />
              <ImageField
                label="Image URL"
                value={content.journey?.image}
                onChange={(v) => updateField('journey', 'image', v)}
              />
              <div className="grid grid-cols-2 gap-3">
                <InputField
                  label="Founded Year"
                  value={content.journey?.foundedYear}
                  onChange={(v) => updateField('journey', 'foundedYear', v)}
                />
                <InputField
                  label="Founded Text"
                  value={content.journey?.foundedText}
                  onChange={(v) => updateField('journey', 'foundedText', v)}
                />
              </div>
            </div>
            
            <div className="border-t border-zinc-800 pt-4 mt-4">
              <div className="flex items-center justify-between mb-4">
                <h4 className="font-semibold text-white">Paragraphs</h4>
                <button
                  onClick={() => {
                    const paragraphs = content.journey?.paragraphs || [];
                    updateField('journey', 'paragraphs', [...paragraphs, 'New paragraph text here...']);
                  }}
                  className="px-3 py-1 bg-yellow-500/20 text-yellow-400 rounded-lg text-sm flex items-center gap-1 hover:bg-yellow-500/30"
                >
                  <Plus className="w-4 h-4" /> Add Paragraph
                </button>
              </div>
              
              {content.journey?.paragraphs?.map((paragraph, index) => (
                <div key={index} className="bg-zinc-800/50 rounded-lg p-4 mb-3">
                  <div className="flex justify-between items-start mb-2">
                    <span className="text-sm text-zinc-400">Paragraph {index + 1}</span>
                    <button
                      onClick={() => {
                        const newParagraphs = content.journey.paragraphs.filter((_, i) => i !== index);
                        updateField('journey', 'paragraphs', newParagraphs);
                      }}
                      className="p-1 hover:bg-red-500/20 rounded text-red-400"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                  <textarea
                    value={paragraph}
                    onChange={(e) => {
                      const newParagraphs = [...content.journey.paragraphs];
                      newParagraphs[index] = e.target.value;
                      updateField('journey', 'paragraphs', newParagraphs);
                    }}
                    rows={3}
                    className="w-full px-4 py-3 bg-zinc-800 border border-zinc-700 rounded-lg text-white placeholder-zinc-500 focus:border-yellow-500 focus:outline-none resize-none"
                  />
                </div>
              ))}
            </div>
          </div>
        </CollapsibleSection>

        {/* Performance Highlights Section */}
        <CollapsibleSection title="Performance Highlights">
          <div className="space-y-4">
            <div className="grid md:grid-cols-2 gap-4">
              <InputField
                label="Section Label"
                value={content.performance?.sectionLabel}
                onChange={(v) => updateField('performance', 'sectionLabel', v)}
              />
              <InputField
                label="Section Title"
                value={content.performance?.title}
                onChange={(v) => updateField('performance', 'title', v)}
              />
            </div>
            
            <div className="border-t border-zinc-800 pt-4 mt-4">
              <div className="flex items-center justify-between mb-4">
                <h4 className="font-semibold text-white">Performance Highlights</h4>
                <button
                  onClick={() => {
                    const highlights = content.performance?.highlights || [];
                    updateField('performance', 'highlights', [...highlights, 'New performance highlight']);
                  }}
                  className="px-3 py-1 bg-amber-500/20 text-amber-400 rounded-lg text-sm flex items-center gap-1 hover:bg-amber-500/30"
                >
                  <Plus className="w-4 h-4" /> Add Highlight
                </button>
              </div>
              
              <div className="grid md:grid-cols-2 gap-3">
                {content.performance?.highlights?.map((highlight, index) => (
                  <div key={index} className="bg-zinc-800/50 rounded-lg p-3 flex items-center gap-3">
                    <Sparkles className="w-5 h-5 text-amber-500 flex-shrink-0" />
                    <input
                      type="text"
                      value={highlight}
                      onChange={(e) => {
                        const newHighlights = [...content.performance.highlights];
                        newHighlights[index] = e.target.value;
                        updateField('performance', 'highlights', newHighlights);
                      }}
                      className="flex-1 bg-transparent border-none text-white focus:outline-none"
                    />
                    <button
                      onClick={() => {
                        const newHighlights = content.performance.highlights.filter((_, i) => i !== index);
                        updateField('performance', 'highlights', newHighlights);
                      }}
                      className="p-1 hover:bg-red-500/20 rounded text-red-400"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </CollapsibleSection>

        {/* Education Section */}
        <CollapsibleSection title="Education / Youth Programs Section">
          <div className="space-y-4">
            <div className="grid md:grid-cols-2 gap-4">
              <InputField
                label="Section Label"
                value={content.education?.sectionLabel}
                onChange={(v) => updateField('education', 'sectionLabel', v)}
              />
              <InputField
                label="Section Title"
                value={content.education?.title}
                onChange={(v) => updateField('education', 'title', v)}
              />
            </div>
            <InputField
              label="Section Description"
              value={content.education?.description}
              onChange={(v) => updateField('education', 'description', v)}
              textarea
            />
            <div className="grid md:grid-cols-2 gap-4">
              <InputField
                label="CTA Button Text"
                value={content.education?.ctaText}
                onChange={(v) => updateField('education', 'ctaText', v)}
              />
              <InputField
                label="CTA Button Link"
                value={content.education?.ctaLink}
                onChange={(v) => updateField('education', 'ctaLink', v)}
              />
            </div>
            
            <div className="border-t border-zinc-800 pt-4 mt-4">
              <div className="flex items-center justify-between mb-4">
                <h4 className="font-semibold text-white">Programs</h4>
                <button
                  onClick={() => addArrayItem('education', 'programs', {
                    title: 'New Program',
                    description: 'Program description'
                  })}
                  className="px-3 py-1 bg-green-500/20 text-green-400 rounded-lg text-sm flex items-center gap-1 hover:bg-green-500/30"
                >
                  <Plus className="w-4 h-4" /> Add Program
                </button>
              </div>
              
              {content.education?.programs?.map((program, index) => (
                <div key={index} className="bg-zinc-800/50 rounded-lg p-4 mb-3">
                  <div className="flex justify-between items-start mb-3">
                    <span className="text-sm text-zinc-400">Program {index + 1}</span>
                    <button
                      onClick={() => removeArrayItem('education', 'programs', index)}
                      className="p-1 hover:bg-red-500/20 rounded text-red-400"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                  <div className="space-y-3">
                    <InputField
                      label="Title"
                      value={program.title}
                      onChange={(v) => updateArrayItem('education', 'programs', index, 'title', v)}
                    />
                    <InputField
                      label="Description"
                      value={program.description}
                      onChange={(v) => updateArrayItem('education', 'programs', index, 'description', v)}
                      textarea
                    />
                  </div>
                </div>
              ))}
            </div>
          </div>
        </CollapsibleSection>

        {/* New Orleans Section */}
        <CollapsibleSection title="New Orleans Connection Section">
          <div className="space-y-4">
            <div className="grid md:grid-cols-2 gap-4">
              <InputField
                label="Section Label"
                value={content.newOrleans?.sectionLabel}
                onChange={(v) => updateField('newOrleans', 'sectionLabel', v)}
              />
              <InputField
                label="Section Title"
                value={content.newOrleans?.title}
                onChange={(v) => updateField('newOrleans', 'title', v)}
              />
            </div>
            <InputField
              label="Section Description"
              value={content.newOrleans?.description}
              onChange={(v) => updateField('newOrleans', 'description', v)}
              textarea
            />
          </div>
        </CollapsibleSection>

        {/* Funder Pitch Section */}
        <CollapsibleSection title="🎯 Funder Pitch Modal (Ancestral Strings Project)">
          <div className="space-y-6">
            <div className="bg-amber-500/10 border border-amber-500/30 rounded-lg p-4 mb-4">
              <p className="text-amber-400 text-sm">
                <strong>Note:</strong> This section controls the hidden funder pitch modal accessible from the "One of a Kind" section link. 
                The PDF Summary at the bottom is a condensed version funders can download.
              </p>
            </div>
            
            {/* Modal Header */}
            <div className="border-b border-zinc-800 pb-4">
              <h4 className="font-semibold text-white mb-3">Modal Header</h4>
              <div className="space-y-3">
                <InputField
                  label="Modal Title"
                  value={content.funderPitch?.modalTitle}
                  onChange={(v) => updateField('funderPitch', 'modalTitle', v)}
                />
                <InputField
                  label="Modal Subtitle"
                  value={content.funderPitch?.modalSubtitle}
                  onChange={(v) => updateField('funderPitch', 'modalSubtitle', v)}
                />
                <InputField
                  label="Opening Hook (Yellow highlight box)"
                  value={content.funderPitch?.openingHook}
                  onChange={(v) => updateField('funderPitch', 'openingHook', v)}
                  textarea
                />
              </div>
            </div>
            
            {/* Why One of a Kind Section */}
            <div className="border-b border-zinc-800 pb-4">
              <h4 className="font-semibold text-white mb-3">Section: Why One of a Kind</h4>
              <div className="space-y-3">
                <InputField
                  label="Section Title"
                  value={content.funderPitch?.whyOneOfAKindTitle}
                  onChange={(v) => updateField('funderPitch', 'whyOneOfAKindTitle', v)}
                />
                <InputField
                  label="Content (use \\n\\n for paragraph breaks)"
                  value={content.funderPitch?.whyOneOfAKindContent}
                  onChange={(v) => updateField('funderPitch', 'whyOneOfAKindContent', v)}
                  textarea
                />
              </div>
            </div>
            
            {/* The Project Section */}
            <div className="border-b border-zinc-800 pb-4">
              <h4 className="font-semibold text-white mb-3">Section: The Project</h4>
              <div className="space-y-3">
                <InputField
                  label="Project Title"
                  value={content.funderPitch?.projectTitle}
                  onChange={(v) => updateField('funderPitch', 'projectTitle', v)}
                />
                <InputField
                  label="Project Description"
                  value={content.funderPitch?.projectDescription}
                  onChange={(v) => updateField('funderPitch', 'projectDescription', v)}
                  textarea
                />
                <div>
                  <div className="flex items-center justify-between mb-2">
                    <label className="text-sm font-medium text-zinc-400">Project Bullet Points</label>
                    <button
                      onClick={() => {
                        const bullets = content.funderPitch?.projectBullets || [];
                        updateField('funderPitch', 'projectBullets', [...bullets, 'New bullet point']);
                      }}
                      className="px-2 py-1 bg-yellow-500/20 text-yellow-400 rounded text-xs flex items-center gap-1 hover:bg-yellow-500/30"
                    >
                      <Plus className="w-3 h-3" /> Add Bullet
                    </button>
                  </div>
                  {content.funderPitch?.projectBullets?.map((bullet, index) => (
                    <div key={index} className="flex items-center gap-2 mb-2">
                      <span className="text-yellow-500">•</span>
                      <input
                        type="text"
                        value={bullet}
                        onChange={(e) => {
                          const newBullets = [...content.funderPitch.projectBullets];
                          newBullets[index] = e.target.value;
                          updateField('funderPitch', 'projectBullets', newBullets);
                        }}
                        className="flex-1 px-3 py-2 bg-zinc-800 border border-zinc-700 rounded-lg text-white text-sm focus:border-yellow-500 focus:outline-none"
                      />
                      <button
                        onClick={() => {
                          const newBullets = content.funderPitch.projectBullets.filter((_, i) => i !== index);
                          updateField('funderPitch', 'projectBullets', newBullets);
                        }}
                        className="p-1 hover:bg-red-500/20 rounded text-red-400"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  ))}
                </div>
                <InputField
                  label="Project Closing Statement"
                  value={content.funderPitch?.projectClosing}
                  onChange={(v) => updateField('funderPitch', 'projectClosing', v)}
                  textarea
                />
              </div>
            </div>
            
            {/* Triple-Threat Section */}
            <div className="border-b border-zinc-800 pb-4">
              <h4 className="font-semibold text-amber-400 mb-3">Section: Triple-Threat Art Pieces</h4>
              <div className="space-y-3">
                <InputField
                  label="Section Title"
                  value={content.funderPitch?.tripleThreatTitle}
                  onChange={(v) => updateField('funderPitch', 'tripleThreatTitle', v)}
                />
                <InputField
                  label="Introduction (70-80% stat)"
                  value={content.funderPitch?.tripleThreatIntro}
                  onChange={(v) => updateField('funderPitch', 'tripleThreatIntro', v)}
                  textarea
                />
                <div className="grid md:grid-cols-3 gap-3">
                  <InputField
                    label="1. Visual Arts Description"
                    value={content.funderPitch?.visualArtsDescription}
                    onChange={(v) => updateField('funderPitch', 'visualArtsDescription', v)}
                    textarea
                  />
                  <InputField
                    label="2. Audio Description"
                    value={content.funderPitch?.audioDescription}
                    onChange={(v) => updateField('funderPitch', 'audioDescription', v)}
                    textarea
                  />
                  <InputField
                    label="3. Performance Description"
                    value={content.funderPitch?.performanceDescription}
                    onChange={(v) => updateField('funderPitch', 'performanceDescription', v)}
                    textarea
                  />
                </div>
                <div className="bg-amber-500/10 border border-amber-500/30 rounded-lg p-4 mt-4">
                  <h5 className="text-amber-400 font-medium mb-2">Kora Water Fountain Example</h5>
                  <InputField
                    label="Example Title"
                    value={content.funderPitch?.koraExampleTitle}
                    onChange={(v) => updateField('funderPitch', 'koraExampleTitle', v)}
                  />
                  <div className="mt-2">
                    <InputField
                      label="Example Content"
                      value={content.funderPitch?.koraExampleContent}
                      onChange={(v) => updateField('funderPitch', 'koraExampleContent', v)}
                      textarea
                    />
                  </div>
                </div>
                <InputField
                  label="Triple-Threat Closing Statement"
                  value={content.funderPitch?.tripleThreatClosing}
                  onChange={(v) => updateField('funderPitch', 'tripleThreatClosing', v)}
                  textarea
                />
              </div>
            </div>
            
            {/* Cultural Impact Section */}
            <div className="border-b border-zinc-800 pb-4">
              <h4 className="font-semibold text-white mb-3">Section: Cultural Impact & Urgency</h4>
              <div className="space-y-3">
                <InputField
                  label="Section Title"
                  value={content.funderPitch?.culturalImpactTitle}
                  onChange={(v) => updateField('funderPitch', 'culturalImpactTitle', v)}
                />
                <InputField
                  label="Content"
                  value={content.funderPitch?.culturalImpactContent}
                  onChange={(v) => updateField('funderPitch', 'culturalImpactContent', v)}
                  textarea
                />
              </div>
            </div>
            
            {/* CTA Section */}
            <div className="border-b border-zinc-800 pb-4">
              <h4 className="font-semibold text-white mb-3">Call to Action</h4>
              <div className="grid md:grid-cols-2 gap-4">
                <InputField
                  label="CTA Title"
                  value={content.funderPitch?.ctaTitle}
                  onChange={(v) => updateField('funderPitch', 'ctaTitle', v)}
                />
                <InputField
                  label="Contact Email"
                  value={content.funderPitch?.contactEmail}
                  onChange={(v) => updateField('funderPitch', 'contactEmail', v)}
                />
              </div>
              <div className="grid md:grid-cols-2 gap-4 mt-3">
                <InputField
                  label="CTA Description"
                  value={content.funderPitch?.ctaDescription}
                  onChange={(v) => updateField('funderPitch', 'ctaDescription', v)}
                  textarea
                />
                <InputField
                  label="Contact Button Text"
                  value={content.funderPitch?.contactButtonText}
                  onChange={(v) => updateField('funderPitch', 'contactButtonText', v)}
                />
              </div>
            </div>
            
            {/* PDF Summary */}
            <div>
              <h4 className="font-semibold text-green-400 mb-3">📄 Downloadable PDF Summary (Max ~1000 words)</h4>
              <p className="text-zinc-500 text-sm mb-3">
                This is the condensed version funders can download as a PDF. Keep it under 1000 words.
              </p>
              <InputField
                label="PDF Summary Content"
                value={content.funderPitch?.pdfSummary}
                onChange={(v) => updateField('funderPitch', 'pdfSummary', v)}
                textarea
              />
              <div className="mt-2 text-sm text-zinc-500">
                Word count: {(content.funderPitch?.pdfSummary || '').split(/\s+/).filter(w => w).length} words
              </div>
            </div>
          </div>
        </CollapsibleSection>

        {/* CTA Section */}
        <CollapsibleSection title="Final Call-to-Action Section">
          <div className="space-y-4">
            <InputField
              label="Title"
              value={content.cta?.title}
              onChange={(v) => updateField('cta', 'title', v)}
            />
            <InputField
              label="Description"
              value={content.cta?.description}
              onChange={(v) => updateField('cta', 'description', v)}
              textarea
            />
            <div className="grid md:grid-cols-2 gap-4">
              <div className="grid grid-cols-2 gap-3">
                <InputField
                  label="Button 1 Text"
                  value={content.cta?.button1Text}
                  onChange={(v) => updateField('cta', 'button1Text', v)}
                />
                <InputField
                  label="Button 1 Link"
                  value={content.cta?.button1Link}
                  onChange={(v) => updateField('cta', 'button1Link', v)}
                />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <InputField
                  label="Button 2 Text"
                  value={content.cta?.button2Text}
                  onChange={(v) => updateField('cta', 'button2Text', v)}
                />
                <InputField
                  label="Button 2 Link"
                  value={content.cta?.button2Link}
                  onChange={(v) => updateField('cta', 'button2Link', v)}
                />
              </div>
            </div>
          </div>
        </CollapsibleSection>

        {/* Bottom Save Button */}
        <div className="flex justify-end pt-6">
          <button
            onClick={saveContent}
            disabled={saving}
            className="px-8 py-3 bg-gradient-to-r from-yellow-500 to-amber-500 hover:from-yellow-400 hover:to-amber-400 text-black font-bold rounded-lg flex items-center gap-2 transition-all disabled:opacity-50"
          >
            <Save className="w-5 h-5" />
            {saving ? 'Saving...' : 'Save All Changes'}
          </button>
        </div>
      </div>
    </div>
  );
}
